// services/api.ts

import { getEndpointUrl, EndpointKey } from "../config/api";

interface ApiOptions {
  method?: string;
  headers?: Record<string, string>;
  body?: any;
  urlParams?: Record<string, string | number>;
  queryParams?: Record<string, string | number>;
  rawResponse?: boolean;
  skipAuth?: boolean;
}

export class ApiError extends Error {
  status: number;
  statusText: string;
  body: any;
  rawResponse?: Response;

  constructor(
    status: number,
    statusText: string,
    message?: string,
    body?: any,
    rawResponse?: Response,
  ) {
    super(message || statusText);
    this.status = status;
    this.statusText = statusText;
    this.body = body;
    this.rawResponse = rawResponse;
    this.name = 'ApiError';
  }
}

class ApiService {
  private static authToken: string | null = null;

  static setAuthToken(token: string) {
    this.authToken = token;
  }

  static clearAuthToken() {
    this.authToken = null;
  }

  private static getHeaders(
    skipAuth = false,
    contentType: string = "application/json",
  ): Record<string, string> {
    const headers: Record<string, string> = {
      "Content-Type": contentType,
    };

    if (!skipAuth) {
      const token = this.authToken || localStorage.getItem("authToken");
      if (!token) {
        throw new Error("No auth token found");
      }
      headers["Authorization"] = `Bearer ${token}`;
    }

    return headers;
  }

  private static appendQueryParams(
    url: string,
    queryParams?: Record<string, string | number>,
  ): string {
    if (!queryParams) return url;

    const searchParams = new URLSearchParams();
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    const queryString = searchParams.toString();
    return queryString
      ? `${url}${url.includes("?") ? "&" : "?"}${queryString}`
      : url;
  }

  static async request<T>(
    endpointKey: EndpointKey,
    options: ApiOptions = {},
    includeAuth: boolean = true,
  ): Promise<T> {
    const {
      method = "GET",
      headers = {},
      body,
      urlParams,
      queryParams,
      rawResponse = false,
      skipAuth = false,
    } = options;
    let url = getEndpointUrl(endpointKey, urlParams);
    url = this.appendQueryParams(url, queryParams);

    const requestHeaders = {
      ...this.getHeaders(skipAuth),
      ...headers,
    };

    try {
      const response = await fetch(url, {
        method,
        headers: requestHeaders,
        body: body ? JSON.stringify(body) : undefined,
      });

      if (!response.ok) {
        let errorData: any;
        let errorMessage: string;
        
        try {
          // Try to parse as JSON first
          const responseText = await response.text();
          
          try {
            errorData = JSON.parse(responseText);
            errorMessage = errorData.error || errorData.message || `Error ${response.status}: ${response.statusText}`;
          } catch (jsonParseError) {
            errorData = { error: responseText };
            errorMessage = responseText || `Error ${response.status}: ${response.statusText}`;
          }
        } catch (textError) {
          console.error('Failed to read response text:', textError);
          errorData = { error: `Error ${response.status}: ${response.statusText}` };
          errorMessage = `Error ${response.status}: ${response.statusText}`;
        }
        
        throw new ApiError(
          response.status,
          response.statusText,
          errorMessage,
          errorData,
          response
        );
      }

      if (response.status === 204) {
        return Promise.resolve() as T;
      }

      return rawResponse ? (response as unknown as T) : response.json();
    } catch (error) {
      // Re-throw ApiErrors, but wrap other errors like network issues
      if (error instanceof ApiError) {
        throw error;
      }
      
      console.error('API request failed:', error);
      throw new Error(error instanceof Error ? error.message : 'Network request failed');
    }
  }

  static async downloadBlob(
    endpointKey: EndpointKey,
    options: ApiOptions = {},
  ): Promise<void> {
    try {
      const url = getEndpointUrl(endpointKey, options.urlParams);
      const token = localStorage.getItem("authToken");

      const response = await fetch(url, {
        method: options.method || "GET",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
        },
        // Add cors mode to ensure headers are accessible
        credentials: "include",
        mode: "cors",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get("Content-Disposition");

      let filename = "download.pdf";
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
      throw error;
    }
  }
}
export default ApiService;
